import { useEffect, useState } from "react";
import {
  Clock,
  Loader2,
  Phone,
  PhoneForwarded,
  PhoneMissed,
  Play,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import {
  getMaxNoAnswerCalls,
  getNoAnswerCooldownHours,
} from "@/lib/projectConfig";
import { getCallSessionCounts } from "@/api/companies";
import { getCallLimit, getCallStats, startCallSession } from "@/api/callCenter";
import type { CallLimit, Company } from "@/api/types";
import type {
  CallOrder,
  CallSession,
  SessionType,
} from "./types";

interface SessionSetupProps {
  slug: string;
  onStart: (session: CallSession) => void;
}

/** Setup state: choose session type, target count, and call order,
 *  with the day's quick stats up top. Fires listCompanies on Start
 *  with the right pipeline_status / sort and hands the resolved
 *  queue to the parent. */
export function SessionSetup({ slug, onStart }: SessionSetupProps) {
  const [sessionType, setSessionType] = useState<SessionType>("new_prospects");
  const [targetCount, setTargetCount] = useState(20);
  const [order, setOrder] = useState<CallOrder>("warmup");
  const [isLoading, setIsLoading] = useState(false);
  // Drives the "available" counts: the backend excludes leads locked
  // to *other* operators relative to this user, so the badges show
  // what's actually startable right now.
  const userId = useAuthStore((s) => s.user?.id);
  // Both numbers are explained to the operator when the retry bucket is
  // empty — an unexplained 0 reads as a broken queue, not as a cooldown.
  const { project } = useCurrentProject();
  const cooldownHours = getNoAnswerCooldownHours(project);
  const maxNoAnswer = getMaxNoAnswerCalls(project);

  // Three buckets in one round-trip — the backend exposes a
  // dedicated endpoint that runs three COUNT(*) FILTER columns in a
  // single query, so the numbers stay in lock-step with what
  // listCompanies(session_type=…) actually returns.
  const [newCount, setNewCount] = useState<number | null>(null);
  const [followUpCount, setFollowUpCount] = useState<number | null>(null);
  const [noAnswerCount, setNoAnswerCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    getCallSessionCounts(slug, userId)
      .then((counts) => {
        if (cancelled) return;
        setNewCount(counts.new_prospects);
        setFollowUpCount(counts.follow_ups);
        setNoAnswerCount(counts.no_answer_retry);
      })
      .catch(() => {
        if (cancelled) return;
        setNewCount(null);
        setFollowUpCount(null);
        setNoAnswerCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, userId]);

  // A salesperson on trial has an allowance of numbers (null limit for everyone else).
  const [callLimit, setCallLimit] = useState<CallLimit | null>(null);
  useEffect(() => {
    let cancelled = false;
    getCallLimit(slug)
      .then((limit) => !cancelled && setCallLimit(limit))
      .catch(() => !cancelled && setCallLimit(null));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Count for the bucket the operator is about to start. `null` means
  // the counts fetch failed — leave Start enabled rather than blocking
  // the session on a number we could not read.
  const selectedBucketCount =
    sessionType === "new_prospects"
      ? newCount
      : sessionType === "follow_ups"
        ? followUpCount
        : noAnswerCount;

  async function handleStart() {
    setIsLoading(true);
    try {
      // Open a locking session: the backend resolves the bucket from
      // `session_type` (same WHERE clause the counts endpoint uses, so
      // the queue matches the card badge), reserves up to `count` leads
      // to this operator, and hands them back. Presentation ordering
      // stays client-side — it's just a reshuffle of the rows we were
      // given, none of which another operator can now be served.
      const { leads, call_limit } = await startCallSession(slug, {
        session_type: sessionType,
        count: targetCount,
      });
      if (call_limit) setCallLimit(call_limit);
      let queue: Company[] = leads;
      if (queue.length === 0) {
        toast.error("No companies available for this session type");
        return;
      }
      if (sessionType === "new_prospects") {
        const scoreOf = (c: Company) => c.effective_score ?? c.score ?? 0;
        if (order === "warmup") {
          queue = queue.slice().sort((a, b) => scoreOf(a) - scoreOf(b));
        } else if (order === "best_first") {
          queue = queue.slice().sort((a, b) => scoreOf(b) - scoreOf(a));
        } else if (order === "random") {
          queue = queue.slice().sort(() => Math.random() - 0.5);
        }
      } else if (sessionType === "no_answer_retry") {
        // Fewest tries first — give leads who only missed once a
        // fresh attempt before chasing leads close to the cap. Reads
        // the first-class `no_answer_attempts` the backend projects
        // onto every CompanyRead, which is the same counter the
        // routing threshold uses.
        queue = queue
          .slice()
          .sort((a, b) => (a.no_answer_attempts ?? 0) - (b.no_answer_attempts ?? 0));
      } else if (sessionType === "follow_ups") {
        // Oldest queued first. The session endpoint takes only
        // session_type + count, so the pipeline_status_changed_at
        // ascending order that used to come from the listCompanies
        // sort params is applied here instead.
        const changedAt = (c: Company) =>
          c.pipeline_status_changed_at
            ? new Date(c.pipeline_status_changed_at).getTime()
            : 0;
        queue = queue.slice().sort((a, b) => changedAt(a) - changedAt(b));
      }
      onStart({
        type: sessionType,
        targetCount,
        order,
        queue,
        currentIndex: 0,
        results: [],
        startedAtMs: Date.now(),
      });
    } catch (err) {
      // A refusal the backend explains (the call limit) is shown as written.
      const detail = axios.isAxiosError(err)
        ? (err.response?.data as { detail?: unknown } | undefined)?.detail
        : null;
      toast.error(typeof detail === "string" ? detail : "Couldn't load the queue");
      console.warn("[CallCenter] queue load failed", err);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <TodayQuickStats slug={slug} />
      <div className="space-y-6 rounded-plane bg-white p-6 shadow-plane">
        <h2 className="text-lg font-semibold text-slate-900">
          New Call Session
        </h2>
        {callLimit?.limit != null && <CallLimitMeter limit={callLimit} />}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <button
            type="button"
            onClick={() => setSessionType("new_prospects")}
            className={cn(
              "rounded-xl border-2 p-4 text-left transition-all",
              sessionType === "new_prospects"
                ? "border-indigo-500 bg-indigo-50"
                : "border-slate-200 hover:border-slate-300",
            )}
          >
            <div className="mb-1 flex items-center gap-2">
              <Phone className="h-5 w-5 text-indigo-500" />
              <span className="font-semibold text-slate-900">
                New Prospects
              </span>
            </div>
            <p className="text-xs text-slate-500">
              First contact with qualified leads
            </p>
            <p className="mt-2 text-sm font-bold text-indigo-600">
              {newCount ?? "—"} available
            </p>
          </button>

          <button
            type="button"
            onClick={() => setSessionType("follow_ups")}
            className={cn(
              "rounded-xl border-2 p-4 text-left transition-all",
              sessionType === "follow_ups"
                ? "border-violet-500 bg-violet-50"
                : "border-slate-200 hover:border-slate-300",
            )}
          >
            <div className="mb-1 flex items-center gap-2">
              <PhoneForwarded className="h-5 w-5 text-violet-500" />
              <span className="font-semibold text-slate-900">Follow-ups</span>
            </div>
            <p className="text-xs text-slate-500">
              Interested leads to recontact
            </p>
            <p className="mt-2 text-sm font-bold text-violet-600">
              {followUpCount ?? "—"} available
            </p>
          </button>

          <button
            type="button"
            onClick={() => setSessionType("no_answer_retry")}
            className={cn(
              "rounded-xl border-2 p-4 text-left transition-all",
              sessionType === "no_answer_retry"
                ? "border-amber-500 bg-amber-50"
                : "border-slate-200 hover:border-slate-300",
            )}
          >
            <div className="mb-1 flex items-center gap-2">
              <PhoneMissed className="h-5 w-5 text-amber-500" />
              <span className="font-semibold text-slate-900">No Answer</span>
            </div>
            <p className="text-xs text-slate-500">
              Retry leads who didn&rsquo;t pick up
            </p>
            <p className="mt-2 text-sm font-bold text-amber-600">
              {noAnswerCount ?? "—"} available
            </p>
            {noAnswerCount === 0 && (
              <p className="mt-1 text-[11px] leading-tight text-slate-400">
                Tried leads return after {cooldownHours}h
              </p>
            )}
          </button>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">
            How many calls?
          </label>
          <div className="flex items-center gap-2">
            {[10, 20, 30, 50].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setTargetCount(n)}
                className={cn(
                  "rounded-lg border px-4 py-2 text-sm font-medium transition-all",
                  targetCount === n
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                )}
              >
                {n}
              </button>
            ))}
            <Input
              type="number"
              value={targetCount}
              onChange={(e) =>
                setTargetCount(
                  Math.max(1, Math.min(200, Number(e.target.value) || 1)),
                )
              }
              className="h-10 w-20 text-center text-sm"
              min={1}
              max={200}
            />
          </div>
        </div>

        {sessionType === "new_prospects" && (
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Call order
            </label>
            <div className="space-y-2">
              {(
                [
                  {
                    value: "warmup",
                    label: "🔥 Warm-up",
                    desc: "Start with easy leads, finish with the best",
                  },
                  {
                    value: "best_first",
                    label: "🎯 Best first",
                    desc: "Highest score first",
                  },
                  {
                    value: "random",
                    label: "🎲 Random",
                    desc: "Shuffle the queue",
                  },
                ] as Array<{ value: CallOrder; label: string; desc: string }>
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setOrder(opt.value)}
                  className={cn(
                    "flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-all",
                    order === opt.value
                      ? "border-indigo-500 bg-indigo-50/50"
                      : "border-slate-200 hover:bg-slate-50",
                  )}
                >
                  <div
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0 rounded-full border-2",
                      order === opt.value
                        ? "border-indigo-500 bg-indigo-500"
                        : "border-slate-300",
                    )}
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-800">
                      {opt.label}
                    </span>
                    <p className="text-xs text-slate-500">{opt.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Explicit empty state. The retry bucket applies a cooldown
          *  server-side, so a lead called this morning is legitimately
          *  gone until tomorrow — without saying so, an operator who
          *  just worked the queue sees 0 and reports a bug. */}
        {sessionType === "no_answer_retry" && noAnswerCount === 0 && (
          <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div className="text-xs text-slate-600">
              <p className="font-medium text-slate-700">
                Nothing to retry right now
              </p>
              <p className="mt-0.5 leading-relaxed">
                A lead that didn&rsquo;t pick up stays out of this queue for{" "}
                {cooldownHours}h, so the ones you called today come back
                tomorrow. After {maxNoAnswer} unanswered calls a lead leaves
                the queue for good: it moves to the email sequence, or is parked
                as Unreachable.
              </p>
            </div>
          </div>
        )}

        <Button
          onClick={handleStart}
          size="lg"
          className="w-full"
          disabled={isLoading || selectedBucketCount === 0}
        >
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Play className="mr-2 h-4 w-4" />
          )}
          Start Session ({targetCount} calls)
        </Button>
      </div>
    </div>
  );
}

/** Mini stats strip above the setup. Reads the same /call-stats
 *  endpoint as the Stats tab so the numbers stay consistent. */
function TodayQuickStats({ slug }: { slug: string }) {
  const [today, setToday] = useState<{
    calls: number;
    interested: number;
    talk_minutes: number;
    reach_rate: number;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    getCallStats(slug, "today").then((res) => {
      if (cancelled) return;
      setToday(res?.today ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!today) return null;
  return (
    <div className="grid grid-cols-4 gap-3">
      <div className="rounded-xl bg-slate-50 p-3 text-center">
        <div className="text-xl font-bold text-slate-900">{today.calls}</div>
        <div className="text-[10px] text-slate-400">Calls today</div>
      </div>
      <div className="rounded-xl bg-emerald-50 p-3 text-center">
        <div className="text-xl font-bold text-emerald-600">
          {today.interested}
        </div>
        <div className="text-[10px] text-emerald-500">Interested</div>
      </div>
      <div className="rounded-xl bg-slate-50 p-3 text-center">
        <div className="text-xl font-bold text-slate-900">
          {today.talk_minutes}m
        </div>
        <div className="text-[10px] text-slate-400">Talk time</div>
      </div>
      <div className="rounded-xl bg-slate-50 p-3 text-center">
        <div className="text-xl font-bold text-slate-900">
          {today.reach_rate}%
        </div>
        <div className="text-[10px] text-slate-400">Reach rate</div>
      </div>
    </div>
  );
}


/** The trial salesperson's allowance: how many numbers the Call Center has
 *  handed them out of their limit, and what happens once it is used up. */
function CallLimitMeter({ limit }: { limit: CallLimit }) {
  const max = limit.limit ?? 0;
  const done = limit.used >= max;
  return (
    <div
      className={cn(
        "rounded-xl border px-4 py-3",
        done ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50",
      )}
    >
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-slate-800">Numbers handed out</span>
        <span className="tabular-nums text-slate-600">
          {limit.used} / {max}
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white">
        <div
          className={cn("h-full rounded-full", done ? "bg-amber-500" : "bg-indigo-500")}
          style={{ width: `${Math.min(100, max ? (limit.used / max) * 100 : 0)}%` }}
        />
      </div>
      <p className={cn("mt-2 text-xs", done ? "text-amber-800" : "text-slate-500")}>
        {done
          ? "You've used all your numbers: you can still call back your own leads. Ask an admin to raise your limit."
          : `${limit.remaining ?? 0} new numbers left. Calling back your own leads doesn't count.`}
      </p>
    </div>
  );
}
